
import { Component, Input, Output, EventEmitter, Inject, OnChanges, SimpleChanges, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Activity, Project, Customer, Employee, Unit, Costcenter } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';
import { ModalService } from '../../../core/services/modal.service';
import moment from 'moment';
import { formatCostcenterLabel } from '../../../shared/utils/costcenter.utils';
import { ALL_EMPLOYEES_OPTION, EMPLOYEE_ACTIVE_FILTER_OPTIONS } from '../../../shared/constants/ui-options';
import {
  ActivityNewPriceDialogResult,
  ActivityNewPriceModalComponent,
} from '../../../shared/components/modal/activity-new-price-modal.component';

@Component({
  selector: 'app-admin-activity-tab',
  standalone: false,
  template: require('./admin-activity-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-activity-tab.component.scss').default],
})
export class AdminActivityTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<keyof Activity> = ['activity_name', 'unit_id', 'start_date'];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() activities: Activity[] = [];
  @Input() projects: Project[] = [];
  @Input() customers: Customer[] = [];
  @Input() employees: Employee[] = [];
  @Input() units: Unit[] = [];
  @Input() costcenters: Costcenter[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  // Filtered lists
  filteredActivities: Activity[] = [];
  filteredProjects: Project[] = [];

  // Filters - object binding for ng-select
  selectedCustomer: Customer | null = null;
  selectedProject: Project | null = null;

  // Form field selections - object binding for ng-select
  selectedEditProject: Project | null = null;
  selectedEditEmployee: { employee_id: number; name: string } | null = null;
  selectedEditUnit: Unit | null = null;
  employeesWithAll: { employee_id: number; name: string }[] = [];

  // Current selection
  currentIndex = 0;
  selectedActivity: Activity | null = null;
  editActivity: Partial<Activity> = {};
  initialEditActivity: Partial<Activity> = {};
  originalId: number | null = null;

  // Moment.js date objects for app-datepicker
  startDateMoment: moment.Moment | null = null;
  stopDateMoment: moment.Moment | null = null;

  // New record mode
  isNew = false;

  activeFilter: 'all' | 'active' | 'inactive' = 'active';
  activeFilterOptions = EMPLOYEE_ACTIVE_FILTER_OPTIONS;

  // Dirty flag
  isDirty = false;

  // Dropdown menu toggle
  showMoreMenu = false;
  private pendingSelectionId: number | null = null;
  private pendingProjectId: number | null = null;
  private pendingSelectionIndex: number | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService,
    @Inject(ModalService) private modalService: ModalService
  ) {}

  getProjectOptionLabel(project: Project | null | undefined): string {
    if (!project) {
      return '';
    }

    return project.endcustomer ? `${project.project_name} (${project.endcustomer})` : project.project_name;
  }

  getActivityOptionLabel(activity: Activity | null | undefined): string {
    if (!activity) {
      return '';
    }

    const employeeName =
      this.employeesWithAll.find((employee) => employee.employee_id === activity.employee_id)?.name || '';
    const start = activity.start_date || '';
    const stop = activity.stop_date || '';
    return `${activity.activity_name}, ${employeeName} (${start} - ${stop})`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employees']) {
      this.rebuildEmployeesWithAll();
    }
    if (changes['activities'] || changes['projects'] || changes['customers']) {
      if (this.projects.length > 0 && this.customers.length > 0) {
        if (this.selectedCustomer === null && this.customers.length > 0) {
          this.selectedCustomer = this.customers[0];
        }
        this.onCustomerFilterChange();
      }
    }
  }

  onCustomerFilterChange(): void {
    if (this.selectedCustomer === null) {
      this.filteredProjects = [...this.projects];
    } else {
      this.filteredProjects = this.projects.filter((p) => p.customer_id === this.selectedCustomer?.customer_id);
    }

    if (this.filteredProjects.length > 0) {
      const preferredProjectId = this.pendingProjectId ?? this.selectedProject?.project_id ?? null;
      this.selectedProject =
        this.filteredProjects.find((p) => p.project_id === preferredProjectId) || this.filteredProjects[0];
    } else {
      this.selectedProject = null;
    }

    this.onProjectFilterChange();
  }

  onProjectFilterChange(): void {
    const projectFiltered =
      this.selectedProject === null
        ? [...this.activities]
        : this.activities.filter((a) => a.project_id === this.selectedProject?.project_id);

    this.filteredActivities = projectFiltered.filter((activity) => {
      if (this.activeFilter === 'active') {
        return activity.stop_date === null;
      }
      if (this.activeFilter === 'inactive') {
        return activity.stop_date !== null;
      }
      return true;
    });

    if (this.isNew || this.isDirty) {
      if (!this.isNew && this.selectedActivity) {
        const selectedIndex = this.filteredActivities.findIndex((a) => a.activity_id === this.selectedActivity?.activity_id);
        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else if (this.filteredActivities.length === 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = Math.min(this.currentIndex, this.filteredActivities.length - 1);
      }

      this.pendingSelectionId = null;
      this.pendingProjectId = null;
      this.pendingSelectionIndex = null;
      return;
    }

    if (this.filteredActivities.length > 0) {
      const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedActivity?.activity_id ?? null;
      const preferredIndex =
        preferredId === null ? -1 : this.filteredActivities.findIndex((a) => a.activity_id === preferredId);

      if (preferredIndex >= 0) {
        this.currentIndex = preferredIndex;
        this.selectActivity(this.filteredActivities[preferredIndex]);
      } else if (this.pendingSelectionIndex !== null) {
        const clampedIndex = Math.min(this.pendingSelectionIndex, this.filteredActivities.length - 1);
        this.currentIndex = clampedIndex;
        this.selectActivity(this.filteredActivities[clampedIndex]);
      } else {
        this.currentIndex = 0;
        this.selectActivity(this.filteredActivities[0]);
      }
    } else {
      this.selectedActivity = null;
      this.editActivity = {};
      this.currentIndex = 0;
    }

    this.pendingSelectionId = null;
    this.pendingProjectId = null;
    this.pendingSelectionIndex = null;
  }

  onActivityFilterChange(): void {
    this.onProjectFilterChange();
  }

  selectActivity(act: Activity): void {
    this.selectedActivity = act;
    this.originalId = act.activity_id;
    this.editActivity = { ...act };
    this.initialEditActivity = { ...act };
    this.selectedEditProject = this.projects.find(p => p.project_id === act.project_id) || null;
    this.selectedEditEmployee = this.employeesWithAll.find(e => e.employee_id === act.employee_id) || null;
    this.selectedEditUnit = this.units.find(u => u.unit_id === act.unit_id) || null;
    this.startDateMoment = act.start_date ? moment(act.start_date) : null;
    this.stopDateMoment = act.stop_date ? moment(act.stop_date) : null;
    this.isNew = false;
    this.isDirty = false;
  }

  onActivitySelect(): void {
    if (this.selectedActivity) {
      const index = this.filteredActivities.findIndex(a => a.activity_id === this.selectedActivity?.activity_id);
      if (index >= 0) {
        this.currentIndex = index;
      }
      this.selectActivity(this.selectedActivity);
    }
  }

  onStartDateChange(newDate: moment.Moment): void {
    this.startDateMoment = newDate;
    this.editActivity.start_date = newDate ? newDate.format('YYYY-MM-DD') : undefined;
    this.onFieldChange();
  }

  onStopDateChange(newDate: moment.Moment): void {
    this.stopDateMoment = newDate;
    this.editActivity.stop_date = newDate ? newDate.format('YYYY-MM-DD') : null;
    this.onFieldChange();
  }

  onEditProjectChange(): void {
    this.editActivity.project_id = this.selectedEditProject?.project_id;
    this.onFieldChange();
  }

  onEditEmployeeChange(): void {
    this.editActivity.employee_id = this.selectedEditEmployee?.employee_id ?? 0;
    this.onFieldChange();
  }

  onEditUnitChange(): void {
    this.editActivity.unit_id = this.selectedEditUnit?.unit_id;
    this.onFieldChange();
  }

  movePrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectActivity(this.filteredActivities[this.currentIndex]);
    }
  }

  moveNext(): void {
    if (this.currentIndex < this.filteredActivities.length - 1) {
      this.currentIndex++;
      this.selectActivity(this.filteredActivities[this.currentIndex]);
    }
  }

  addNew(): void {
    const defaultUnit = this.units.find((u) => u.description_s === 'debit') || this.units[0] || null;

    this.isNew = true;
    this.selectedActivity = null;
    this.originalId = null;
    this.selectedEditProject = this.selectedProject;
    this.selectedEditEmployee = this.employeesWithAll.find(e => e.employee_id === 0) || null;
    this.selectedEditUnit = defaultUnit;
    this.editActivity = {
      activity_name: '',
      project_id: this.selectedProject?.project_id,
      employee_id: 0,
      unit_id: defaultUnit?.unit_id ?? 1,
      salary_code: null,
      costcenter: null,
      start_date: new Date().toISOString().split('T')[0],
      currency_id: 1,
    };
    this.initialEditActivity = { ...this.editActivity };
    this.startDateMoment = this.editActivity.start_date ? moment(this.editActivity.start_date) : null;
    this.stopDateMoment = null;
    this.isDirty = false;
  }

  save(): void {
    if (this.isNew) {
      this.adminService.createActivity(this.editActivity).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.insertId ?? null;
          this.pendingProjectId = this.editActivity.project_id ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'aktivitet', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateActivity(this.originalId, this.editActivity).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.pendingSelectionId = this.originalId;
          this.pendingProjectId = this.editActivity.project_id ?? this.selectedProject?.project_id ?? null;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'aktivitet', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.filteredActivities.length > 0) {
        this.selectActivity(this.filteredActivities[this.currentIndex]);
      } else {
        this.editActivity = {};
        this.initialEditActivity = {};
        this.selectedEditProject = null;
        this.selectedEditEmployee = null;
        this.selectedEditUnit = null;
        this.startDateMoment = null;
        this.stopDateMoment = null;
      }
    } else if (this.selectedActivity) {
      this.selectActivity(this.selectedActivity);
      return;
    }
    this.isDirty = false;
  }

  async delete(): Promise<void> {
    if (this.originalId === null) {
      return;
    }

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort denna aktivitet?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteActivity(this.originalId!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'aktivitet', err),
    });
  }

  async openNewPriceModal(): Promise<void> {
    if (!this.selectedActivity || this.originalId === null) {
      return;
    }

    const selectedProject = this.projects.find((p) => p.project_id === this.selectedActivity?.project_id) || null;
    const selectedEmployee = this.employeesWithAll.find((e) => e.employee_id === this.selectedActivity?.employee_id) || null;
    const selectedUnit = this.units.find((u) => u.unit_id === this.selectedActivity?.unit_id) || null;
    const selectedCostcenter =
      this.costcenters.find((cc) => String(cc.external_id) === this.selectedActivity?.costcenter) || null;

    const result = (await this.modalService.show(ActivityNewPriceModalComponent, {
      activity: this.selectedActivity,
      projectLabel: this.getProjectOptionLabel(selectedProject),
      employeeLabel: selectedEmployee?.name ?? 'Okänd',
      unitLabel: selectedUnit?.description_s ?? 'Okänd',
      costcenterLabel: selectedCostcenter ? formatCostcenterLabel(selectedCostcenter) : 'Inget',
    })) as ActivityNewPriceDialogResult | undefined;

    if (!result) {
      return;
    }

    this.adminService.newPriceActivity(this.originalId, result).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.pendingSelectionId = response.new_activity_id;
        this.pendingProjectId = this.selectedActivity?.project_id ?? null;
        this.isDirty = false;
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('uppdatera', 'aktivitet', err),
    });
  }

  duplicate(): void {
    if (!this.selectedActivity) return;
    this.isNew = true;
    this.originalId = null;
    this.editActivity = {
      ...this.selectedActivity,
      activity_id: undefined,
      activity_name: this.selectedActivity.activity_name + ' (kopia)',
    };
    this.initialEditActivity = { ...this.selectedActivity };
    this.selectedEditProject = this.projects.find(p => p.project_id === this.editActivity.project_id) || null;
    this.selectedEditEmployee = this.employeesWithAll.find(e => e.employee_id === this.editActivity.employee_id) || null;
    this.selectedEditUnit = this.units.find(u => u.unit_id === this.editActivity.unit_id) || null;
    this.startDateMoment = this.editActivity.start_date ? moment(this.editActivity.start_date) : null;
    this.stopDateMoment = this.editActivity.stop_date ? moment(this.editActivity.stop_date) : null;
    this.isDirty = true;
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: keyof Activity): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: keyof Activity): boolean {
    if (!this.isNew || !this.isRequiredField(field)) {
      return false;
    }
    const value = this.editActivity[field];
    return typeof value === 'string' ? value.trim() === '' : value == null;
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  hasInvalidDateRange(): boolean {
    const start = this.editActivity.start_date;
    const stop = this.editActivity.stop_date;
    return typeof start === 'string' && typeof stop === 'string' && start !== '' && stop !== '' && start > stop;
  }

  isFieldDirty(field: keyof Activity): boolean {
    return this.normalizeFieldValue(this.editActivity[field]) !== this.normalizeFieldValue(this.initialEditActivity[field]);
  }

  hasChanges(): boolean {
    const fields = new Set<keyof Activity>([
      ...Object.keys(this.editActivity),
      ...Object.keys(this.initialEditActivity),
    ] as Array<keyof Activity>);
    return Array.from(fields).some((field) => this.isFieldDirty(field));
  }

  private normalizeFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  canSave(): boolean {
    if (!this.isDirty) {
      return false;
    }
    if (this.hasInvalidDateRange()) {
      return false;
    }
    if (!this.isNew) {
      return true;
    }
    return this.requiredCreateFields.every((field) => !this.isRequiredMissing(field));
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedActivity;
  }

  canOpenNewPriceModal(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedActivity;
  }

  getEmployeeName(employeeId: number | null | undefined): string {
    if (employeeId === null || employeeId === undefined) return '';
    if (employeeId === 0) return 'Alla anställda';
    const emp = this.employees.find((e) => e.employee_id === employeeId);
    return emp?.name || '';
  }

  getUnitName(unitId: number | undefined): string {
    if (!unitId) return '';
    const unit = this.units.find((u) => u.unit_id === unitId);
    return unit?.description_s || '';
  }

  private rebuildEmployeesWithAll(): void {
    this.employeesWithAll = [
      ALL_EMPLOYEES_OPTION,
      ...this.employees
        .filter((employee) => employee.employee_id !== 0)
        .map((employee) => ({ employee_id: employee.employee_id, name: employee.name })),
    ];

    if (this.selectedEditEmployee) {
      this.selectedEditEmployee =
        this.employeesWithAll.find((employee) => employee.employee_id === this.selectedEditEmployee?.employee_id) || null;
    }
  }

  protected readonly formatCostcenterLabel = formatCostcenterLabel;
}
